import { VFC } from "react";
import { Link as RouterLink } from "@tanstack/react-location";
import { Button } from "src/components/button";
import { Cluster } from "src/components/cluster";
import { css } from "src/styles/theme";
import { ExamplePlayground } from "src/features/playground";
import { Subtitle, Title, Link, ListItem } from "src/components/typography";
import { AppMenu } from "src/components/app-menu";
import { APP_CONFIG } from "src/constants";
import { CheckCircledIcon } from "@radix-ui/react-icons";
import canvasImg from "./intro-canvas.svg";
import outputImg from "./intro-output.svg";
import falsoPackageJson from "@ngneat/falso/package.json";

export const IntroPage: VFC = () => {
  return (
    <>
      <AppMenu />
      <div className={$hero()}>
        <div className={$intro()}>
          <Subtitle as="h1">Welcome to Fako</Subtitle>
          <Title as="h2" css={{ marginTop: "$4" }}>
            A visual fake data generator
          </Title>

          <ul className={$features()}>
            {features.map((feature, index) => (
              <ListItem
                key={index}
                size={{ "@initial": "base", "@medium": "lg" }}
                css={{ lineHeight: 1.6 }}
                weight="light"
              >
                <CheckCircledIcon width={24} height={24} />
                <span>{feature}</span>
              </ListItem>
            ))}
          </ul>
        </div>
      </div>
      <div className={$playground()}>
        <img className={$imgCanvas()} src={canvasImg} alt="" />
        <img className={$imgOutput()} src={outputImg} alt="" />
        <ExamplePlayground />
      </div>
      <Cluster justify="center" className={$actions()}>
        <Button
          as={RouterLink}
          look="primary"
          color="primary"
          to="/app"
          size="large"
        >
          Open Fako
        </Button>
      </Cluster>

      <footer className={$footer()}>
        <div>
          Made with love by{" "}
          <Link
            href={APP_CONFIG.authorWebsiteUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Sandro Roth
          </Link>
        </div>
        <div>
          Data is generated with{" "}
          <Link
            href={falsoPackageJson.repository.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {falsoPackageJson.name} (v{falsoPackageJson.version}){" "}
          </Link>
        </div>
      </footer>
    </>
  );
};

const features = [
  <>
    <strong>Fake data generation</strong> for prototyping, mocking and testing
  </>,
  <>
    Over <strong>200 data generators</strong> for generating names, addresses
    and much more
  </>,
  <>
    <strong>Deeply nested</strong> data structures via drag &amp; drop,
    visualized on the canvas
  </>,
  <>
    <strong>Conditional fields</strong> and <strong>composed values</strong> by
    using variables and expressions
  </>,
];

const $hero = css({
  $$overlap: "16rem",

  marginBottom: "-$$overlap",
  paddingTop: "$16",
  paddingBottom: "$$overlap",
  backgroundColor: "$primaryBg",

  "@medium": {
    paddingTop: "$30",
  },
});

const $intro = css({
  maxWidth: "82rem",
  margin: "0 auto",
  padding: "0 $4",
});

const $features = css({
  margin: "$10 0 $24",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "$6",

  li: {
    display: "flex",

    "& > svg": {
      flex: "0 0 auto",
      marginRight: "$3",
      transform: "translateY(2.5px)",
      opacity: 0.75,
    },
  },
});

const $actions = css({
  position: "sticky",
  bottom: "$8",
  zIndex: 1,

  a: {
    boxShadow: "$4",
    borderRadius: "$full",
  },
});

const $playground = css({
  width: "100%",
  maxWidth: "82rem",
  margin: "0 auto $24",
  padding: "0 $4",
  position: "relative",
});

const $imgCanvas = css({
  display: "none",
  width: "12rem",
  position: "absolute",
  top: "$26",
  right: "calc(100% - $4)",
  pointerEvents: "none",
  userSelect: "none",

  "@media (min-width: 105rem)": {
    display: "block",
  },
});

const $imgOutput = css({
  display: "none",
  width: "10rem",
  position: "absolute",
  bottom: "$4",
  left: "calc(100% - $12)",
  pointerEvents: "none",
  userSelect: "none",

  "@media (min-width: 105rem)": {
    display: "block",
  },
});

const $footer = css({
  margin: "$20 0 $12",
  padding: "0 $4",
  color: "$neutralText",
  fontSize: "$sm",
  textAlign: "center",

  "& > * + *": {
    marginTop: "$2",
  },
});
